import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import { canAccessRoute } from '@/utils/validateByUrl';
import {
  selectAdvisorAllowedRoutes,
  canAccessAdvisorRoute,
} from '@/modules/advisor/selectors/advisorNavigation';
import { selectIsAdvisorWidgetRouteGranted } from '@/modules/advisor/utils/advisorWidgetRoutes';

/**
 * Hook to check if the user has permission to navigate to a given path.
 * Shared with `PagePermissionGuard` (which calls this hook), so a link this
 * hook approves can never land on Forbidden.
 *
 * For the Advisor role the check derives from the same nav artifact the
 * sidebar renders (`selectAdvisorAllowedRoutes` / `canAccessAdvisorRoute`):
 * clickable ⇔ allowed by construction. Other roles — and an advisor whose
 * menu hasn't loaded — fall back to `canAccessRoute` (backend-menu
 * validation, fail-closed on a missing/empty menu, dashboard excepted).
 * An advisor page listed in `advisorWidgetRoutes.ts` is also allowed when its
 * widget is in the cached home dashboard widget list.
 *
 * @param path - The full path to check (e.g., "/Investor/Portfolio/Diversification")
 * @returns true if the user can navigate to the path, false otherwise
 *
 * @example
 * ```tsx
 * const canViewDiversification = useCanNavigateTo("/Investor/Portfolio/Diversification");
 *
 * return canViewDiversification && <FooterButton ... />;
 * ```
 */
export function useCanNavigateTo(path: string): boolean {
  const menu = useSelector((s: WARootState) => s.appView.UserInfo?.Menu);
  const grantedHiddenPaths = useSelector(
    (s: WARootState) => s.appView.grantedHiddenPaths,
  );
  const currentRole = useSelector((s: WARootState) => s.auth.currentRole);
  const advisorAllowedRoutes = useSelector(selectAdvisorAllowedRoutes);
  const isWidgetGranted = useSelector((s: WARootState) =>
    selectIsAdvisorWidgetRouteGranted(s, path),
  );
  const isMenuGranted = advisorAllowedRoutes
    ? canAccessAdvisorRoute(path, advisorAllowedRoutes)
    : canAccessRoute(path, currentRole, menu, grantedHiddenPaths);
  return isMenuGranted || isWidgetGranted;
}
